import { canScopeRequestCredits } from '../../common/credits-backend/scope';
import type { CreditsBackendSnapshot } from '../../common/credits-backend/types';
import type {
	CreditsBannerStateChange,
	CreditsBannerState,
} from '../../common/credits-ui-state/types';

export const deriveCreditsBannerState = ({
	denial,
	warning,
}: Pick<CreditsBackendSnapshot, 'denial' | 'warning'>): CreditsBannerState => {
	if (denial === null) {
		return warning
			? {
					balanceState: 'warning',
					depletionBehavior: null,
					scope: warning.scope,
					...(warning.resetsAt !== undefined ? { resetDate: warning.resetsAt } : {}),
				}
			: {
					balanceState: 'healthy',
					depletionBehavior: null,
				};
	}

	return {
		balanceState: 'depleted',
		depletionBehavior: 'block',
		scope: denial.scope,
		...(denial.resetsAt !== undefined ? { resetDate: denial.resetsAt } : {}),
		...(denial.requestMethod !== undefined && canScopeRequestCredits(denial.scope)
			? { requestMethod: denial.requestMethod, canRequestCredits: true }
			: {}),
	};
};

const hasDepletedBalance = ({ balanceState }: CreditsBannerState): boolean =>
	balanceState === 'depleted';

const hasBlockedDepletionBehavior = ({
	balanceState,
	depletionBehavior,
}: CreditsBannerState): boolean => balanceState === 'depleted' && depletionBehavior === 'block';

const CREDITS_BANNER_STATE_COMPARISON_KEYS = {
	balanceState: true,
	depletionBehavior: true,
	scope: true,
	resetDate: true,
	canRequestCredits: true,
} satisfies Record<Exclude<keyof CreditsBannerState, 'requestMethod'>, true>;

const creditsBannerStateComparisonKeys = Object.keys(CREDITS_BANNER_STATE_COMPARISON_KEYS) as Array<
	keyof typeof CREDITS_BANNER_STATE_COMPARISON_KEYS
>;

const getRequestMethodKey = ({ requestMethod }: CreditsBannerState): string | undefined =>
	requestMethod?.mode === 'REDIRECT_URL'
		? `${requestMethod.mode}:${requestMethod.redirectUrl}`
		: requestMethod?.mode;

export const areCreditsBannerStatesEqual = (
	previousState: CreditsBannerState,
	nextState: CreditsBannerState,
): boolean =>
	creditsBannerStateComparisonKeys.every(
		(comparisonKey) => previousState[comparisonKey] === nextState[comparisonKey],
	) && getRequestMethodKey(previousState) === getRequestMethodKey(nextState);

/**
 * Compares the previous and next credits banner states.
 *
 * Use `nextState` for rendering. Use these flags only for one-time reactions
 * such as blocking input, recording analytics, or resetting a dismissed
 * warning banner.
 */
export const deriveCreditsBannerStateChange = (
	previousState: CreditsBannerState | null,
	nextState: CreditsBannerState,
): CreditsBannerStateChange => {
	if (previousState === null) {
		// The first accepted snapshot is current state, not a newly-entered
		// state. Do not fire entry effects for a warning/block that already
		// existed before the provider mounted.
		return {
			isInitial: true,
			enteredHealthy: false,
			enteredDepleted: false,
			enteredBlocked: false,
			shouldResetDismissal: false,
		};
	}

	return {
		isInitial: false,
		enteredHealthy:
			previousState.balanceState !== 'healthy' && nextState.balanceState === 'healthy',
		enteredDepleted: !hasDepletedBalance(previousState) && hasDepletedBalance(nextState),
		enteredBlocked:
			!hasBlockedDepletionBehavior(previousState) && hasBlockedDepletionBehavior(nextState),
		shouldResetDismissal:
			previousState.balanceState !== nextState.balanceState ||
			previousState.depletionBehavior !== nextState.depletionBehavior ||
			previousState.scope !== nextState.scope,
	};
};
